import { Headset } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

import { useAuth } from "@/app/hooks/useAuth";
import { useSupportRequestCounts } from "@/app/hooks/useSupportRequestCounts";
import { useShowSupportRequests } from "@/app/hooks/useShowSupportRequests";
import { cn } from "@/app/utils/cn";

import { SupportRequestsChart } from "./SupportRequestsChart";

export const SupportModeCard = () => {
  const { isSupport, isAdmin, isSuperAdmin } = useAuth();
  const { pathname } = useLocation();

  const isActive = pathname === "/support";

  // admin e super admin também usam a tela pela busca, mesmo sem fila de chamados
  const canOpenSupport = isSupport || isAdmin || isSuperAdmin;
  const showSupportRequests = useShowSupportRequests();

  // o card é o dono do polling: é ele que vive montado em todas as telas
  const { open: openCount } = useSupportRequestCounts({
    enabled: canOpenSupport && showSupportRequests,
    poll: true,
  });

  if (!canOpenSupport) return null;

  if (!showSupportRequests) {
    return (
      <div className="pt-5">
        <Link
          to="/support"
          className={cn(
            "flex h-11 w-52 items-center gap-2 px-6 py-2 font-semibold",
            isActive ? "rounded-full bg-primary text-white" : "text-black04",
          )}
        >
          <Headset className="h-6 w-6 shrink-0" />

          <p>Suporte</p>
        </Link>
      </div>
    );
  }

  const hasOpenRequests = !isActive && openCount > 0;

  return (
    <div className="pt-5">
      <Link
        to="/support"
        className={cn(
          "flex w-52 flex-col gap-2 rounded-xl border px-4 py-3 font-semibold transition-colors",
          isActive
            ? "border-primary bg-primary text-white"
            : hasOpenRequests
              ? "border-primary/40 bg-primary/5 text-primary hover:bg-primary/10"
              : "border-gray01 text-black04 hover:bg-gray-50",
        )}
      >
        <div className="flex items-center gap-2">
          <Headset className="h-6 w-6 shrink-0" />

          <div className="flex min-w-0 flex-1 flex-col leading-tight">
            <p>Suporte</p>
            <span
              className={cn(
                "text-[10px] font-medium",
                isActive ? "text-white/80" : "text-black04",
              )}
            >
              {openCount === 0
                ? "Nenhum chamado aberto"
                : `${openCount} ${
                    openCount === 1 ? "chamado aberto" : "chamados abertos"
                  }`}
            </span>
          </div>

          {hasOpenRequests && (
            <span className="relative flex size-2.5 shrink-0" aria-hidden>
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
              <span className="relative inline-flex size-2.5 rounded-full bg-primary" />
            </span>
          )}
        </div>

        <SupportRequestsChart isActive={isActive} isMuted={openCount === 0} />
      </Link>
    </div>
  );
};
